<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import { formatTableTime } from './utils';
import AgentCell from './AgentCell.vue';

const { agentTotalCount, agentMetrics, isLoading, pageIndex } = defineProps({
  agentTotalCount: {
    type: Number,
    default: 0,
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();

const tableData = computed(() => {
  return agentMetrics
    .map(item => {
      const { metric: _metric } = item ?? {};
      const metric = _metric ?? {};
      return {
        name: item.name,
        availability: item.availability, // online, offline, busy
        firstSignInTime: formatTableTime(metric.firstSignInTime), // 首次登录时间
        onlineTime: metric.onlineTime, // 在线时长 "5h 32min"
        busyTime: metric.busyTime, // 忙碌时长 "30min"
        assigned: metric.assigned, // 分配会话数 20
        resolved: metric.resolved, // 已处理会话数 10
        avgFirstResponseTime: metric.avgFirstResponseTime, // 平均首次响应时间 "10min"
        avgResolveTime: metric.avgResolveTime, // 平均解决时间 "15min"
        csat: metric.csat,
        open: metric.open || 0, // 开放会话数
        onHold: metric.onHold || 0,
      };
    });
});

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? 'whitespace-normal break-words' : 'text-slate-300 dark:text-slate-700',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'),
    cell: cellProps => {
      return h(AgentCell, {
        name: cellProps.row.original.name,
        availability: cellProps.row.original.availability,
      });
    },

    size: 260,
  }),

  columnHelper.accessor('firstSignInTime', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.FIRST_SIGN_IN_TIME'),
    cell: defaulSpanRender,
    size: 160,
  }),
  
  columnHelper.accessor('onlineTime', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.ONLINE_TIME'),
    cell: defaulSpanRender,
    size: 120,
  }),
  
  columnHelper.accessor('busyTime', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.BUSY_TIME'),
    cell: defaulSpanRender,
    size: 120,
  }),
  
  columnHelper.accessor('assigned', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.ASSIGNED'),
    cell: defaulSpanRender,
    size: 100,
  }),
  
  columnHelper.accessor('resolved', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.RESOLVED'),
    cell: defaulSpanRender,
    size: 100,
  }),
  
  columnHelper.accessor('open', {
    header: 'Open',
    cell: defaulSpanRender,
    size: 100,
  }),

  columnHelper.accessor('onHold', {
    header: 'On Hold',
    cell: defaulSpanRender,
    size: 100,
  }),
  
  columnHelper.accessor('avgFirstResponseTime', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AVG_FIRST_RESPONSE_TIME'),
    cell: defaulSpanRender,
    size: 230,
  }),
  
  columnHelper.accessor('avgResolveTime', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AVG_RESOLVE_TIME'),
    cell: defaulSpanRender,
    size: 180,
  }),
  
  columnHelper.accessor('csat', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.CSAT'),
    cell: defaulSpanRender,
    size: 80,
  }),
];

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    // 先用简单方式去除分页逻辑
    pageSize: 999,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return agentTotalCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagintaion = updater(paginationParams.value);
    emit('pageChange', newPagintaion.pageIndex);
  },
});
</script>

<template>
  <div class="agent-table-container">
    <div class="overflow-x-auto shadow-[inset_-8px_0_8px_-4px_#ddd]">
      <Table :table="table" fixed class="max-h-[calc(100vh-21.875rem)]" />
    </div>
    <Pagination class="mt-2" :table="table" />
    <div v-if="isLoading" class="agents-loader">
      <Spinner />
      <span>{{
        $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
      }}</span>
    </div>
    <EmptyState
      v-else-if="!isLoading && !agentMetrics.length"
      :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
    />
  </div>
</template>

<style lang="scss" scoped>
.agent-table-container {
  @apply flex flex-col flex-1 w-full;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        @apply text-sm rounded-xl;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &::v-deep .ve-pagination {
    @apply bg-transparent dark:bg-transparent;
  }

  &::v-deep .ve-pagination-select {
    @apply hidden;
  }

  .row-user-block {
    @apply items-center flex text-left;

    .user-block {
      @apply items-start flex flex-col min-w-0 my-0 mx-2;

      .title {
        @apply text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100;
      }

      .sub-title {
        @apply text-xs text-slate-600 dark:text-slate-200;
      }
    }
  }

  .table-pagination {
    @apply mt-4 text-right;
  }

  :deep(.availability) {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    &.availability__online {
      @apply bg-green-400 dark:bg-green-400;
    }
    &.availability__busy {
      @apply bg-yellow-500 dark:bg-yellow-500;
    }
    &.availability__offline {
      @apply bg-slate-500 dark:bg-slate-500;
    }
  }
}

.agents-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
